export const en = {
  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    viewAll: 'View all',
    learnMore: 'Learn more',
    getStarted: 'Get started',
    contactUs: 'Contact us',
    readMore: 'Read more',
    backTo: 'Back to',
    yes: 'Yes',
    no: 'No',
    or: 'or',
    and: 'and',
    theme: 'Theme',
    error: 'Error loading data',
    tryAgain: 'Something went wrong. Please try again.',
    retry: 'Try again',
  },

  // Navigation
  nav: {
    home: 'Home',
    services: {
      title: 'Services',
      all: 'All services',
      crm: 'CRM Solutions',
      app: 'App Development',
      web: 'Web Development',
      sms: 'SMS Marketing',
      voice: 'AI Voice Agent',
    },
    caseStudies: 'Case Studies',
    blog: 'Blog',
    about: 'About',
    contact: 'Contact Us',
    admin: 'Admin',
    login: 'Log in',
    logout: 'Log out',
  },

  // Home page
  home: {
    seo: {
      title: 'Your partner for digital growth',
      description: 'Din AI Bedrift helps Norwegian businesses with CRM, app development, web development, SMS marketing and AI voice agents.',
    },
    hero: {
      subtitle: 'B2B Digital Services',
      title: 'Digital solutions that',
      titleHighlight: 'drive growth',
      description: 'We help Norwegian businesses succeed digitally. From CRM and app development to AI-powered solutions - we are your partner for digital transformation.',
      cta: 'Get started',
      secondaryCta: 'View our services',
      form: {
        title: 'Get started today',
        subtitle: 'Fill out the form and we will contact you within 24 hours.',
        name: 'Your name',
        email: 'Email address',
        company: 'Company (optional)',
        message: 'Describe your project...',
        submit: 'Send inquiry',
        submitting: 'Sending...',
        successTitle: 'Thank you for your inquiry!',
        successMessage: 'We will contact you soon.',
      },
    },
    stats: {
      customers: 'Happy customers',
      projects: 'Projects delivered',
      satisfaction: 'Customer satisfaction',
      support: 'Support',
    },
    partners: 'Partners',
    quote: 'We help businesses digitalize and automate to save time and increase revenue.',
    intro: {
      prefix: 'We help businesses',
      highlight1: 'digitalize',
      middle: 'and',
      highlight2: 'automate',
      suffix: 'to save time and increase revenue.',
    },
    services: {
      title: 'Our services',
      subtitle: 'Complete digital solutions tailored to your needs',
      viewAll: 'View all services',
    },
    whyUs: {
      title: 'Why choose Din AI Bedrift?',
      description: 'We combine Norwegian business understanding with international technology expertise to deliver solutions that actually work.',
      experience: 'years experience',
      points: [
        'Experienced developers and designers',
        'Tailored solutions for Norwegian businesses',
        'Transparent communication and pricing',
        'Long-term partnership and support',
        'Modern technology and best practices',
        'GDPR-compliant solutions',
      ],
      button: 'About us',
    },
    caseStudies: {
      title: 'Our success stories',
      subtitle: 'See how we have helped other businesses reach their goals',
      viewAll: 'View all case studies',
    },
    testimonials: {
      title: 'What customers say',
      subtitle: 'We are proud of the feedback from our customers',
    },
    cta: {
      title: 'Ready to take the next step?',
      description: 'Let us discuss how we can help your business grow digitally.',
      button: 'Contact us',
      secondaryButton: 'View services',
    },
  },

  // Services page
  services: {
    title: 'Our services',
    subtitle: 'What we offer',
    description: 'We deliver complete digital solutions tailored to your needs. From idea to implementation, we are with you every step of the way.',
    seoDescription: 'Explore our digital services - CRM, app development, web development, SMS marketing and AI voice agents.',
    popular: 'Popular',
    readMore: 'Read more about',
    features: 'Features',
    featuresTitle: 'What does this service include?',
    benefits: 'Benefits',
    faq: 'Frequently asked questions',
    service: 'Service',
    otherServices: 'Other services',
    viewAll: 'View all services',
    notFound: {
      title: 'Service not found',
      description: 'Sorry, we couldn\'t find the service you\'re looking for.',
    },
    cta: {
      title: 'Not sure what you need?',
      description: 'We\'d be happy to help you find the right solution for your business. Contact us for a no-obligation conversation.',
      button: 'Contact us',
      interested: 'Interested?',
      interestedDesc: 'Contact us for a no-obligation conversation about how we can help you.',
      ready: 'Ready to get started?',
      readyDesc: 'Let\'s discuss how',
      readyDescSuffix: 'can help your business.',
    },
  },

  // Blog page
  blog: {
    title: 'Blog',
    subtitle: 'Insights and articles',
    description: 'Stay updated with the latest trends in digital transformation, technology, and business development.',
    seoDescription: 'Read our latest articles on digital transformation, technology, and business development.',
    readTime: 'min read',
    categories: 'Categories',
    all: 'All',
    allPosts: 'All posts',
    noPosts: 'No articles found',
    noPostsCategory: 'No articles in category',
    noPostsYet: 'There are no published articles yet.',
    viewAll: 'View all articles',
    relatedPosts: 'Related articles',
    backToBlog: 'Back to blog',
    tags: 'Tags',
    draft: 'Draft',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    notFound: {
      title: 'Article not found',
      description: 'Sorry, we couldn\'t find the article you\'re looking for.',
    },
    newsletter: {
      title: 'Stay updated',
      description: 'Get the latest articles and insights directly in your inbox.',
    },
    cta: {
      title: 'Need help with digital transformation?',
      description: 'Let\'s discuss how we can help your business grow.',
    },
  },

  // Case Studies page
  caseStudies: {
    title: 'Case studies',
    subtitle: 'Success stories',
    description: 'See how we have helped Norwegian businesses achieve their digital goals.',
    seoDescription: 'Explore our success stories and see how we have helped businesses with digital transformation.',
    client: 'Client',
    industry: 'Industry',
    duration: 'Duration',
    servicesUsed: 'Services used',
    challenge: 'The challenge',
    solution: 'The solution',
    results: 'Results',
    testimonial: 'Testimonial',
    technologies: 'Technologies used',
    gallery: 'Gallery',
    featured: 'Featured',
    readMore: 'Read full case',
    backToCaseStudies: 'Back to case studies',
    moreCaseStudies: 'More case studies',
    viewAll: 'View all case studies',
    noCases: 'No case studies yet',
    noCasesDesc: 'We are working on documenting our success stories. Check back soon!',
    notFound: {
      title: 'Case study not found',
      description: 'Sorry, we couldn\'t find the case study you\'re looking for.',
    },
    cta: {
      title: 'Become our next success story',
      description: 'Let\'s discuss how we can help your business achieve similar results.',
      button: 'Contact us',
      ready: 'Ready for similar results?',
      readyDesc: 'Let\'s discuss how we can help your business.',
    },
  },

  // About page
  about: {
    title: 'About us',
    subtitle: 'Who we are',
    description: 'We are a team of experienced developers, designers, and strategists passionate about helping Norwegian businesses succeed digitally.',
    story: {
      title: 'Our story',
      content: 'Din AI Bedrift was founded with a clear vision: to make advanced digital solutions accessible to Norwegian businesses of all sizes.',
    },
    values: {
      title: 'Our values',
      quality: 'Quality first',
      qualityDesc: 'We never compromise on quality.',
      partnership: 'Partnership',
      partnershipDesc: 'We see our customers as partners.',
      innovation: 'Innovation',
      innovationDesc: 'We always stay up to date.',
      passion: 'Passion',
      passionDesc: 'We are passionate about what we do.',
    },
    timeline: {
      title: 'Our journey',
    },
    whyUs: {
      title: 'Why choose us?',
      description: 'We combine Norwegian business understanding with international technology expertise.',
    },
  },

  // Contact page
  contact: {
    title: 'Contact us',
    subtitle: 'We\'d love to hear from you',
    description: 'Have a project in mind? Get in touch for a no-obligation conversation about how we can help you.',
    formTitle: 'Send us a message',
    form: {
      name: 'Name *',
      namePlaceholder: 'Your full name',
      email: 'Email *',
      emailPlaceholder: 'your@email.com',
      phone: 'Phone',
      phonePlaceholder: '+47 123 45 678',
      company: 'Company',
      companyPlaceholder: 'Your company Ltd',
      companySize: {
        label: 'Company size',
        placeholder: 'Select size',
        small: '1-10 employees',
        medium: '11-50 employees',
        large: '51-200 employees',
        enterprise: '200+ employees',
      },
      service: 'How can we help you?',
      servicePlaceholder: 'Select service',
      other: 'Other',
      message: 'Message *',
      messagePlaceholder: 'Tell us about your project or needs...',
      submit: 'Send message',
      submitting: 'Sending...',
      required: '* Required fields',
      sendNew: 'Send another message',
      validation: {
        nameMin: 'Name must be at least 2 characters',
        emailInvalid: 'Invalid email address',
        messageMin: 'Message must be at least 10 characters',
      },
    },
    success: {
      title: 'Thank you for your inquiry!',
      description: 'We have received your message and will contact you as soon as possible, usually within 24 hours on business days.',
    },
    info: {
      title: 'Contact information',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      hours: {
        title: 'Business hours',
        weekdays: 'Monday - Friday: 09:00 - 17:00',
        weekend: 'Saturday - Sunday: Closed',
      },
    },
  },

  // Admin
  admin: {
    common: {
      manage: 'Manage',
      filterByStatus: 'Filter by status',
      allStatuses: 'All statuses',
      published: 'Published',
      draft: 'Draft',
      featured: 'Featured',
      noDescription: 'No description',
      notPublished: 'Not published',
      readingTime: 'min read',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {page} of {total}',
      confirmDelete: 'Are you sure you want to delete "{name}"?',
      confirmDeleteGeneric: 'Are you sure you want to delete this?',
      cannotUndo: 'This action cannot be undone.',
      deleteConfirm: 'Delete',
      interestedIn: 'Interested in',
      received: 'Received',
      unpublish: 'Unpublish',
      publish: 'Publish',
      markFeatured: 'Mark as featured',
      removeFeatured: 'Remove from featured',
    },
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
      overview: 'Here is an overview of your website',
      recentLeads: 'Recent leads',
      recentPosts: 'Recent blog posts',
      leadStatusOverview: 'Lead status overview',
      quickActions: 'Quick actions',
      viewAll: 'View all',
      ofTotal: 'of {total} total',
      noCategory: 'No category',
      newLeads: 'New leads',
      blogPosts: 'Blog posts',
      services: 'Services',
      caseStudies: 'Case studies',
      newBlogPost: 'New blog post',
      viewLeads: 'View leads',
      editServices: 'Edit services',
      proposalSent: 'Proposal sent',
    },
    sidebar: {
      dashboard: 'Dashboard',
      pages: 'Pages',
      services: 'Services',
      blog: 'Blog',
      caseStudies: 'Case Studies',
      testimonials: 'Testimonials',
      leads: 'Leads',
      media: 'Media',
      settings: 'Settings',
      profile: 'Profile',
    },
    layout: {
      adminPanel: 'Admin Panel',
      viewSite: 'View site',
      language: 'Language',
      logout: 'Log out',
    },
    editor: {
      // Blog post editor
      title: 'Title',
      titleRequired: 'Title is required',
      slug: 'Slug',
      slugRequired: 'Slug is required',
      excerpt: 'Excerpt',
      excerptHint: 'Short description shown in list view',
      category: 'Category',
      selectCategory: 'Select category',
      coverImageUrl: 'Cover image URL',
      content: 'Content',
      contentPlaceholder: 'Write your content here...',
      seoSettings: 'SEO Settings',
      seoTitle: 'SEO Title',
      seoTitleHint: 'Overrides default title in search results',
      seoDescription: 'SEO Description',
      seoDescriptionHint: 'Description shown in search results',
      keywords: 'Keywords',
      keywordsHint: 'Comma-separated keywords',
      status: 'Status',
      cancel: 'Cancel',
      saveChanges: 'Save changes',
      createPost: 'Create post',
      createService: 'Create service',
      // Service editor
      basicInfo: 'Basic information',
      shortDescription: 'Short description',
      icon: 'Icon',
      order: 'Order',
      featuredService: 'Featured service',
      detailedDescription: 'Detailed description',
      detailedDescPlaceholder: 'Write a detailed description of the service...',
      features: 'Features',
      benefits: 'Benefits',
      faq: 'Frequently asked questions',
      addItem: 'Add',
      featureTitle: 'Title',
      featureDescription: 'Description',
      question: 'Question',
      answer: 'Answer',
      callToAction: 'Call to Action',
      ctaText: 'CTA text',
      ctaLink: 'CTA link',
      heroImageUrl: 'Hero image URL',
      // Categories
      categories: {
        crm: 'CRM',
        technology: 'Technology',
        marketing: 'Marketing',
        design: 'Design',
        tips: 'Tips',
      },
      // Rich text editor
      bold: 'Bold',
      italic: 'Italic',
      heading1: 'Heading 1',
      heading2: 'Heading 2',
      heading3: 'Heading 3',
      bulletList: 'Bullet list',
      numberedList: 'Numbered list',
      quote: 'Quote',
      link: 'Link',
      image: 'Image',
      undo: 'Undo',
      redo: 'Redo',
      startWriting: 'Start writing...',
      enterUrl: 'URL:',
      enterImageUrl: 'Image URL:',
    },
    blog: {
      title: 'Blog',
      subtitle: 'Manage blog posts',
      newPost: 'New post',
      editPost: 'Edit post',
      deletePost: 'Delete post',
      status: 'Status',
      draft: 'Draft',
      published: 'Published',
      noPosts: 'No blog posts',
      noPostsDesc: 'You haven\'t created any blog posts yet.',
      createFirst: 'Create your first post',
    },
    services: {
      title: 'Services',
      subtitle: 'Manage your services',
      newService: 'New service',
      editService: 'Edit service',
      deleteService: 'Delete service',
      noServices: 'No services',
      noServicesDesc: 'You haven\'t created any services yet.',
      createFirst: 'Create your first service',
    },
    leads: {
      title: 'Leads',
      subtitle: 'Manage inquiries and leads',
      deleteLead: 'Delete lead',
      noLeads: 'No leads found',
      noLeadsDesc: 'You have no leads yet',
      noLeadsFilter: 'Try a different filter',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      companySize: 'Company size',
      interestedIn: 'Interested in',
      message: 'Message',
      status: {
        new: 'New',
        contacted: 'Contacted',
        qualified: 'Qualified',
        proposal: 'Proposal',
        won: 'Won',
        lost: 'Lost',
      },
    },
    caseStudies: {
      title: 'Case studies',
      subtitle: 'Manage case studies and success stories',
      newCase: 'New case study',
      editCase: 'Edit case study',
      deleteCase: 'Delete case study',
      noCases: 'No case studies',
      noCasesDesc: 'You haven\'t created any case studies yet.',
      createFirst: 'Create your first case study',
    },
    testimonials: {
      title: 'Testimonials',
      subtitle: 'Manage customer testimonials and recommendations',
      newTestimonial: 'New testimonial',
      editTestimonial: 'Edit testimonial',
      deleteTestimonial: 'Delete testimonial',
      noTestimonials: 'No testimonials',
      noTestimonialsDesc: 'You haven\'t added any customer testimonials yet.',
      createFirst: 'Add your first testimonial',
      deleteConfirm: 'Are you sure you want to delete this review from {name}?',
    },
    media: {
      title: 'Media',
      subtitle: 'Manage images and files',
      upload: 'Upload',
      deleteFile: 'Delete file',
      noFiles: 'No media files',
      noFilesDesc: 'You haven\'t uploaded any files yet.',
      uploadFirst: 'Upload your first file',
    },
    pages: {
      title: 'Pages',
      subtitle: 'Manage static pages',
      deletePage: 'Delete page',
      noPages: 'No pages',
      noPagesDesc: 'You haven\'t created any pages yet.',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Manage global website settings',
      general: 'General',
      generalDesc: 'Basic website information',
      siteName: 'Site name',
      siteNameRequired: 'Site name is required',
      siteDescription: 'Site description',
      logoUrl: 'Logo URL',
      logoDarkUrl: 'Logo URL (dark mode)',
      contact: 'Contact information',
      contactDesc: 'Contact details displayed on the website',
      email: 'Email',
      emailInvalid: 'Invalid email address',
      phone: 'Phone',
      address: 'Address',
      social: 'Social media',
      socialDesc: 'Links to social media profiles',
      linkedin: 'LinkedIn',
      twitter: 'Twitter',
      facebook: 'Facebook',
      instagram: 'Instagram',
      footer: 'Footer',
      footerText: 'Footer text',
      footerHint: 'Displayed at the bottom of all pages',
      saveSettings: 'Save settings',
      saved: 'Saved!',
    },
    profile: {
      title: 'Profile',
      subtitle: 'Manage your account settings',
      info: 'Profile information',
      infoDesc: 'Update your account information',
      fullName: 'Full name',
      email: 'Email',
      emailHint: 'Email address cannot be changed',
      saveChanges: 'Save changes',
      saved: 'Saved!',
      role: 'Role',
      roleAdmin: 'Administrator',
      roleEditor: 'Editor',
      changePassword: 'Change password',
      changePasswordDesc: 'Update your login password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      passwordChanged: 'Password changed!',
      validation: {
        nameMin: 'Name must be at least 2 characters',
        emailInvalid: 'Invalid email address',
        passwordMin: 'Password must be at least 6 characters',
        passwordMatch: 'Passwords must match',
      },
    },
  },

  // Auth
  auth: {
    login: {
      title: 'Log in',
      subtitle: 'Welcome back',
      email: 'Email',
      password: 'Password',
      submit: 'Log in',
      forgotPassword: 'Forgot password?',
      error: 'Invalid email or password',
    },
    logout: 'Log out',
  },

  // Footer
  footer: {
    services: 'Services',
    company: 'Company',
    legal: 'Legal',
    privacy: 'Privacy',
    terms: 'Terms',
    copyright: '© 2026 Din AI Bedrift. All rights reserved.',
    description: 'We help Norwegian businesses with digital solutions that drive growth. CRM, app development, web design, SMS marketing and AI voice assistants.',
  },

  // Errors
  errors: {
    notFound: {
      title: 'Page not found',
      description: 'Sorry, we couldn\'t find the page you\'re looking for. It may have been moved or deleted.',
      button: 'Go to home',
      goBack: 'Go back',
    },
    generic: 'Something went wrong',
  },

  // Legal pages
  legal: {
    privacy: {
      title: 'Privacy Policy',
      seoDescription: 'Read our privacy policy and how we process your personal data.',
      lastUpdated: 'Last updated: January 2024',
    },
    terms: {
      title: 'Terms and Conditions',
      seoDescription: 'Read our terms and conditions for using our services.',
      lastUpdated: 'Last updated: January 2024',
    },
  },
};
